import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, ProtectedRoute } from '../components/layout';
import { Button, Card, CardBody, LoadingSpinner, Modal, UserAvatar } from '../components/common';
import { EditGameModal } from '../components/game';
import { StoryModal, PdfExportModal } from '../components/story';
import { CharacterCard, CharacterModal, CharacterViewModal } from '../components/characters';
import { SceneModal, SceneCard } from '../components/scenes';
import { useGame, useUpdateGame } from '../hooks/useGames';
import { useCharacters, useCreateCharacter, useUpdateCharacter, useDeleteCharacter } from '../hooks/useCharacters';
import { useScenes, useCreateScene, useUpdateScene, useDeleteScene } from '../hooks/useScenes';
import { usePluginEnabled } from '../hooks/usePlugins';

export const GameSetup = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGame(id);
  const updateGame = useUpdateGame();
  const { data: charactersData } = useCharacters(id);
  const createCharacter = useCreateCharacter(id);
  const updateCharacterMutation = useUpdateCharacter(id);
  const deleteCharacter = useDeleteCharacter(id);

  // Scene hooks
  const { data: scenesData } = useScenes(id);
  const createScene = useCreateScene(id);
  const updateSceneMutation = useUpdateScene(id);
  const deleteSceneMutation = useDeleteScene(id);

  // Check if PDF export plugin is enabled
  const { isEnabled: isPdfExportEnabled } = usePluginEnabled('pdf-export');

  const [copied, setCopied] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isStoryModalOpen, setIsStoryModalOpen] = useState(false);
  const [isCharacterModalOpen, setIsCharacterModalOpen] = useState(false);
  const [isCharacterViewModalOpen, setIsCharacterViewModalOpen] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [editingCharacter, setEditingCharacter] = useState(null);
  const [viewingCharacter, setViewingCharacter] = useState(null);
  const [isSceneModalOpen, setIsSceneModalOpen] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [isScenesExpanded, setIsScenesExpanded] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  // Update browser tab title with game name
  useEffect(() => {
    if (data?.game?.title) {
      document.title = `${data.game.title} - Fate After Dice`;
    }
    return () => {
      document.title = 'FAD - Fate After Dice';
    };
  }, [data?.game?.title]);

  const handleStartGame = async () => {
    try {
      await updateGame.mutateAsync({ id, data: { status: 'active' } });
      navigate(`/games/${id}/play`);
    } catch (err) {
      console.error('Failed to start game:', err);
    }
  };

  if (isLoading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
            <LoadingSpinner size="lg" />
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  if (error || !data?.game) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="max-w-4xl mx-auto px-4 py-8 text-center">
            <p className="text-red-600 mb-4">Game not found or access denied</p>
            <Button onClick={() => navigate('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  const game = data.game;
  const isGM = game.myRole === 'gm';

  const copyCode = async () => {
    if (game.inviteCode) {
      await navigator.clipboard.writeText(game.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAddPlayers = async () => {
    await copyCode();
    setIsInviteModalOpen(true);
  };

  const handleEditSave = async (updateData) => {
    try {
      await updateGame.mutateAsync({ id, data: updateData });
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update game:', err);
    }
  };

  const handleCharacterSave = async (characterData) => {
    try {
      if (editingCharacter) {
        await updateCharacterMutation.mutateAsync({
          characterId: editingCharacter.id || editingCharacter._id,
          ...characterData,
        });
      } else {
        await createCharacter.mutateAsync(characterData);
      }
      setIsCharacterModalOpen(false);
      setEditingCharacter(null);
    } catch (err) {
      console.error('Failed to save character:', err);
    }
  };

  const handleCharacterDelete = async (characterId) => {
    try {
      await deleteCharacter.mutateAsync(characterId);
      setIsCharacterModalOpen(false);
      setEditingCharacter(null);
    } catch (err) {
      console.error('Failed to delete character:', err);
    }
  };

  const handleEditCharacter = (character) => {
    setEditingCharacter(character);
    setIsCharacterModalOpen(true);
  };

  const handleViewCharacter = (character) => {
    setViewingCharacter(character);
    setIsCharacterViewModalOpen(true);
  };

  // Scene handlers
  const handleSceneSave = async (sceneData) => {
    try {
      if (editingScene) {
        await updateSceneMutation.mutateAsync({
          sceneId: editingScene.id || editingScene._id,
          ...sceneData,
        });
      } else {
        await createScene.mutateAsync(sceneData);
      }
      setIsSceneModalOpen(false);
      setEditingScene(null);
    } catch (err) {
      console.error('Failed to save scene:', err);
    }
  };

  const handleSceneDelete = async (sceneId) => {
    try {
      await deleteSceneMutation.mutateAsync(sceneId);
      setIsSceneModalOpen(false);
      setEditingScene(null);
    } catch (err) {
      console.error('Failed to delete scene:', err);
    }
  };

  const handleEditScene = (scene) => {
    setEditingScene(scene);
    setIsSceneModalOpen(true);
  };

  const characters = charactersData?.characters || [];
  const scenes = scenesData?.scenes || [];
  const currentScene = scenes.length > 0 ? scenes[scenes.length - 1] : null;

  return (
    <ProtectedRoute>
      <Layout>
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-900 mb-6 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
            {/* Left Column - Game Info */}
            <div className="min-w-0 overflow-hidden">
              <Card>
                <CardBody>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-2xl font-bold">{game.title}</h1>
                    <span className={`
                      px-2 py-1 text-xs font-medium rounded-full uppercase flex-shrink-0
                      ${game.status === 'setup' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}
                    `}>
                      {game.status}
                    </span>
                  </div>

                  {game.description && (
                    <p className="text-gray-600 mb-4">{game.description}</p>
                  )}

                  {(game.outcome || game.premise?.setting || game.premise?.tone) && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2 mb-4">
                      {game.outcome && (
                        <p className="text-sm">
                          <span className="font-medium">Outcome:</span> {game.outcome}
                        </p>
                      )}
                      {game.premise?.setting && (
                        <p className="text-sm">
                          <span className="font-medium">Setting:</span> {game.premise.setting}
                        </p>
                      )}
                      {game.premise?.tone && (
                        <p className="text-sm">
                          <span className="font-medium">Tone:</span> {game.premise.tone}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsStoryModalOpen(true)}
                    >
                      View Story
                    </Button>
                    {isPdfExportEnabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsPdfExportModalOpen(true)}
                      >
                        Export PDF
                      </Button>
                    )}
                    {isGM && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsEditModalOpen(true)}
                      >
                        Edit Details
                      </Button>
                    )}
                  </div>

                  {/* Go to Game Button (active games) */}
                  {game.status === 'active' && (
                    <div className="mt-4 pt-4 border-t">
                      <Button
                        onClick={() => navigate(`/games/${id}/play`)}
                        className="w-full"
                      >
                        Go to Game
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Characters Section */}
              <Card className="mt-6">
                <CardBody>
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="font-semibold">Characters ({characters.length})</h2>
                    {isGM && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setEditingCharacter(null);
                          setIsCharacterModalOpen(true);
                        }}
                      >
                        + Add Character
                      </Button>
                    )}
                  </div>

                  {characters.length === 0 ? (
                    <p className="text-gray-500 text-sm">No characters created yet.</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                      {characters.map((character) => (
                        <CharacterCard
                          key={character.id || character._id}
                          character={character}
                          onClick={() => handleViewCharacter(character)}
                          onEdit={isGM ? handleEditCharacter : undefined}
                          showAspects={true}
                        />
                      ))}
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Scenes Section */}
              <Card className="mt-6">
                <CardBody>
                  {/* Header with expand toggle */}
                  <div className="flex justify-between items-center mb-4">
                    <button
                      type="button"
                      className="flex items-center gap-2 text-left"
                      onClick={() => setIsScenesExpanded(!isScenesExpanded)}
                    >
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${isScenesExpanded ? 'rotate-90' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <h2 className="font-semibold">Scenes ({scenes.length})</h2>
                    </button>
                    {isGM && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          setEditingScene(null);
                          setIsSceneModalOpen(true);
                        }}
                      >
                        + Add Scene
                      </Button>
                    )}
                  </div>

                  {scenes.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      No scenes created yet. {isGM ? 'Add scenes to organize your story, or they will be created automatically when you start rounds.' : 'The GM will set up scenes for the story.'}
                    </p>
                  ) : (
                    <>
                      {/* Current/Latest Scene Summary (always visible) */}
                      {!isScenesExpanded && currentScene && (
                        <div
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                          onClick={() => setIsScenesExpanded(true)}
                        >
                          <span className="flex-shrink-0 w-8 h-8 bg-gray-700 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {currentScene.number}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900">
                              {currentScene.title || `Scene ${currentScene.number}`}
                            </p>
                            {currentScene.description && (
                              <p className="text-sm text-gray-500 truncate">{currentScene.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              {currentScene.characterIds?.length > 0 && (
                                <span>{currentScene.characterIds.length} character{currentScene.characterIds.length !== 1 ? 's' : ''}</span>
                              )}
                              {currentScene.aspects?.length > 0 && (
                                <span>{currentScene.aspects.length} aspect{currentScene.aspects.length !== 1 ? 's' : ''}</span>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-gray-400">Click to expand</span>
                        </div>
                      )}

                      {/* Expanded: All scenes list */}
                      {isScenesExpanded && (
                        <div className="space-y-3">
                          {scenes.map((scene) => (
                            <SceneCard
                              key={scene.id || scene._id}
                              scene={scene}
                              isGM={isGM}
                              isCurrent={(scene.id || scene._id) === (currentScene?.id || currentScene?._id)}
                              onEdit={isGM ? handleEditScene : undefined}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Right Column - Participants */}
            <div className="min-w-0 overflow-hidden">
              {/* Participants */}
              <Card>
                <CardBody>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold">
                      Participants ({1 + (game.playerUserIds?.length || 0)}/{game.settings?.maxPlayers || 6})
                    </h2>
                    {isGM && game.inviteCode && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={handleAddPlayers}
                      >
                        + Add Players
                      </Button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {/* GM */}
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <div className="flex items-center gap-3">
                        <UserAvatar
                          displayName={game.gmUserId?.displayName}
                          avatarUrl={game.gmUserId?.avatarUrl}
                          size="sm"
                          isGM
                        />
                        <span>{game.gmUserId?.displayName}</span>
                      </div>
                      <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded">
                        GM
                      </span>
                    </div>

                    {/* Players */}
                    {game.playerUserIds?.map((player) => (
                      <div key={player.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            displayName={player.displayName}
                            avatarUrl={player.avatarUrl}
                            size="sm"
                          />
                          <span>{player.displayName}</span>
                        </div>
                      </div>
                    ))}

                    {(!game.playerUserIds || game.playerUserIds.length === 0) && (
                      <p className="text-gray-500 text-sm py-2">
                        Waiting for players to join...
                      </p>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Start Game Button (GM only, setup status) */}
              {isGM && game.status === 'setup' && (
                <Card className="mt-6">
                  <CardBody className="text-center">
                    <p className="text-sm text-gray-600 mb-4">
                      {game.playerUserIds?.length >= 1
                        ? 'Ready to start!'
                        : 'Need at least 1 player to start.'}
                    </p>
                    <Button
                      onClick={handleStartGame}
                      disabled={!game.playerUserIds || game.playerUserIds.length < 1 || updateGame.isPending}
                      loading={updateGame.isPending}
                      className="w-full"
                    >
                      Start Game
                    </Button>
                    {updateGame.isError && (
                      <p className="text-red-600 text-sm mt-2">
                        Failed to start game. Please try again.
                      </p>
                    )}
                  </CardBody>
                </Card>
              )}

            </div>
          </div>
        </div>

          {/* Edit Game Modal */}
          <EditGameModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            game={game}
            onSave={handleEditSave}
            isSaving={updateGame.isPending}
          />

          {/* Story Modal */}
          <StoryModal
            isOpen={isStoryModalOpen}
            onClose={() => setIsStoryModalOpen(false)}
            gameId={id}
          />

          {/* Character Modal */}
          <CharacterModal
            isOpen={isCharacterModalOpen}
            onClose={() => {
              setIsCharacterModalOpen(false);
              setEditingCharacter(null);
            }}
            gameId={id}
            character={editingCharacter}
            onSave={handleCharacterSave}
            onDelete={isGM ? handleCharacterDelete : undefined}
            isSaving={createCharacter.isPending || updateCharacterMutation.isPending}
          />

          {/* Character View Modal */}
          <CharacterViewModal
            isOpen={isCharacterViewModalOpen}
            onClose={() => {
              setIsCharacterViewModalOpen(false);
              setViewingCharacter(null);
            }}
            character={viewingCharacter}
            onEdit={handleEditCharacter}
            canEdit={isGM}
          />

          {/* PDF Export Modal */}
          <PdfExportModal
            isOpen={isPdfExportModalOpen}
            onClose={() => setIsPdfExportModalOpen(false)}
            gameId={id}
          />

          {/* Scene Modal */}
          <SceneModal
            isOpen={isSceneModalOpen}
            onClose={() => {
              setIsSceneModalOpen(false);
              setEditingScene(null);
            }}
            scene={editingScene}
            characters={characters}
            onSave={handleSceneSave}
            onDelete={isGM ? handleSceneDelete : undefined}
            isSaving={createScene.isPending || updateSceneMutation.isPending}
            canDelete={isGM && editingScene && !editingScene.hasRounds}
          />

          {/* Invite Players Modal */}
          <Modal
            isOpen={isInviteModalOpen}
            onClose={() => setIsInviteModalOpen(false)}
            title="Invite Players"
            size="sm"
          >
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                Share this code with players to invite them:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <span className="text-2xl font-mono tracking-widest">
                  {game.inviteCode}
                </span>
              </div>
              <p className="text-sm text-green-600 mb-4">
                {copied ? '✓ Copied to clipboard!' : 'Code copied to clipboard'}
              </p>
              <Button
                variant="secondary"
                onClick={copyCode}
                className="w-full"
              >
                Copy Again
              </Button>
            </div>
          </Modal>
      </Layout>
    </ProtectedRoute>
  );
};
